import { create } from "zustand";
import {
  getPlanBoard,
  getEmployeeDay as getEmployeeDayApi,
  getTaskChain as getTaskChainApi
} from "@/services/HrService/PlanReportService";
import { registerStoreReset } from "@/store/storeResetUtils";
import { TaskStatus } from "@/store/Employee/DailyPlanStore";

/** The one status a person/date resolves to. Mirrors the backend DAY_STATUS. */
export type DayStatus =
  | 'holiday'
  | 'on_leave'
  | 'weekend'
  | 'no_plan'
  | 'punched_no_plan'
  | 'in_progress'
  | 'forgot_eod'
  | 'eod_done';

export interface BoardRow {
  employee_id: string;
  name: string;
  email: string;
  role_name: string;
  dept_id: number | null;
  dept_name: string | null;
  manager_id: string | null;
  manager_name: string | null;
  has_left: boolean;

  is_working_day: boolean;
  is_holiday: boolean;
  on_leave: boolean;
  day_status: DayStatus;

  punch_in: string | null;
  punch_out: string | null;
  total_hours: number | string | null;
  /** punch_out was synthesized by a retro EOD — total_hours is not measured. */
  is_retro: boolean;
  is_late: boolean;
  has_note: boolean;

  plan_submitted: boolean;
  eod_submitted: boolean;

  tasks_total: number;
  completed: number;
  not_completed: number;
  carried: number;
  dropped: number;
  still_open: number;
  resolved: number;
  /** null (not 0) when nothing is resolved yet — render as an em-dash. */
  completion_rate: number | null;
}

export interface BoardSummary {
  total: number;
  expected_to_work: number;
  planned: number;
  not_planned: number;
  punched_in: number;
  eod_done: number;
  forgot_eod: number;
  in_progress: number;
  on_leave: number;
  late: number;
  tasks_total: number;
  completed: number;
  not_completed: number;
  carried: number;
  dropped: number;
  still_open: number;
  resolved: number;
  completion_rate: number | null;
}

export interface Board {
  date: string;
  /** The server's business date in company tz. Never derive "today" on the client. */
  today: string;
  timezone: string;
  scope: 'org' | 'team';
  is_company_working_day: boolean;
  summary: BoardSummary;
  rows: BoardRow[];
}

export interface DayTask {
  id: number;
  plan_date: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  incomplete_reason: string | null;
  drop_reason: string | null;
  carried_from_task_id: number | null;
  origin_task_id: number | null;
  origin_date: string | null;
  carry_count: number;
  age_days: number;
  sort_order: number;
}

export interface EmployeeDay {
  employee: {
    id: string;
    name: string;
    email: string;
    role_name: string;
    dept_name: string | null;
    has_left: boolean;
  };
  attendance: {
    date: string;
    punch_in: string | null;
    punch_out: string | null;
    total_hours: number | string | null;
    daily_status: string | null;
    is_retro: boolean;
    remarks: string | null;
  } | null;
  tasks: DayTask[];
}

export interface ChainEntry {
  id: number;
  plan_date: string;
  title: string;
  status: TaskStatus;
  incomplete_reason: string | null;
  drop_reason: string | null;
  carry_count: number;
}

interface PlanBoardStore {
  board: Board | null;
  dayDetail: EmployeeDay | null;
  chain: ChainEntry[] | null;

  loading: boolean;
  detailLoading: boolean;
  error: string | null;

  fetchBoard: (params: {
    date?: string;
    deptId?: string | number;
    search?: string;
    scope?: 'org' | 'team';
  }) => Promise<void>;
  fetchEmployeeDay: (employeeId: string, date: string, scope?: 'org' | 'team') => Promise<void>;
  fetchTaskChain: (taskId: number) => Promise<void>;
  clearDayDetail: () => void;
  clearError: () => void;
  resetStore: () => void;
}

const usePlanBoardStore = create<PlanBoardStore>((set) => ({
  board: null,
  dayDetail: null,
  chain: null,
  loading: false,
  detailLoading: false,
  error: null,

  fetchBoard: async (params) => {
    try {
      set({ loading: true, error: null });
      const response = await getPlanBoard(params);
      if (response.success && response.data) {
        set({ board: response.data, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to load the plan board', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  fetchEmployeeDay: async (employeeId, date, scope) => {
    try {
      set({ detailLoading: true, error: null, chain: null });
      const response = await getEmployeeDayApi(employeeId, date, scope);
      if (response.success && response.data) {
        set({ dayDetail: response.data, detailLoading: false });
      } else {
        set({
          error: response.error?.message || "Failed to load the employee's day",
          detailLoading: false
        });
      }
    } catch (error: any) {
      set({ error: error.message, detailLoading: false });
    }
  },

  fetchTaskChain: async (taskId) => {
    try {
      const response = await getTaskChainApi(taskId);
      if (response.success && response.data) {
        set({ chain: response.data.chain || [] });
      }
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  clearDayDetail: () => set({ dayDetail: null, chain: null }),
  clearError: () => set({ error: null }),
  resetStore: () =>
    set({
      board: null,
      dayDetail: null,
      chain: null,
      loading: false,
      detailLoading: false,
      error: null
    })
}));

registerStoreReset('planBoard', () => usePlanBoardStore.getState().resetStore());

export default usePlanBoardStore;
